import { supabase } from '../../../config/supabase';
import { buildTenantPath } from '../../../utils/tenantSlug';
import {
  isAssessmentV2QuestionnaireTest,
  type AssessmentV2TestKey,
} from '../registry/assessmentRegistry';
import type {
  AssessmentInviteV2,
  AssessmentInviteV2Preview,
  AssessmentInviteV2StartPayload,
  AssessmentProfessionalContext,
  AssessmentResultV2,
} from '../types';
import { AssessmentSessionsV2Service } from './AssessmentSessionsV2Service';

const DEFAULT_INVITE_TTL_HOURS = 48;
const PUBLIC_INVITE_TOKEN_BYTES = 24;

const normalizeJsonObject = (value: unknown): Record<string, any> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, any>) : {};

const normalizeResult = (item: any): AssessmentResultV2 => ({
  ...item,
  status: item?.status ?? 'completed',
  testVersion: item?.testVersion ?? 1,
  groupIndex: item?.groupIndex ?? 1,
  groupTotal: item?.groupTotal ?? 1,
  summaryJson: normalizeJsonObject(item?.summaryJson),
  payloadJson: normalizeJsonObject(item?.payloadJson),
  assetsJson: normalizeJsonObject(item?.assetsJson),
});

const normalizeInvite = (item: any): AssessmentInviteV2 => ({
  inviteId: item.inviteId,
  clientId: item.clientId,
  sessionId: item.sessionId,
  patientId: item.patientId,
  tokenHash: item.tokenHash,
  selectedTests: Array.isArray(item.selectedTests) ? item.selectedTests : [],
  status: item.status ?? 'active',
  expiresAt: item.expiresAt,
  openedAt: item.openedAt ?? null,
  startedAt: item.startedAt ?? null,
  completedAt: item.completedAt ?? null,
  revokedAt: item.revokedAt ?? null,
  revokedReason: item.revokedReason ?? null,
  failedAttempts: typeof item.failedAttempts === 'number' ? item.failedAttempts : 0,
  lockedUntil: item.lockedUntil ?? null,
  createdByUserId: item.createdByUserId ?? null,
  metadataJson: normalizeJsonObject(item.metadataJson),
  createdAt: item.createdAt,
  updatedAt: item.updatedAt ?? null,
});

const normalizePreview = (data: any): AssessmentInviteV2Preview => ({
  inviteId: data.inviteId,
  clientId: data.clientId,
  clientName: data.clientName,
  clientSlug: data.clientSlug,
  sessionId: data.sessionId,
  status: data.status,
  selectedTests: Array.isArray(data.selectedTests) ? data.selectedTests : [],
  expiresAt: data.expiresAt,
  openedAt: data.openedAt ?? null,
  startedAt: data.startedAt ?? null,
  completedAt: data.completedAt ?? null,
  lockedUntil: data.lockedUntil ?? null,
  progressCount: typeof data.progressCount === 'number' ? data.progressCount : 0,
  progressTotal: typeof data.progressTotal === 'number' ? data.progressTotal : 0,
  patientLabel: data.patientLabel || 'Paciente',
});

const normalizeStartPayload = (data: any): AssessmentInviteV2StartPayload => ({
  ...normalizePreview(data),
  patientId: data.patientId,
  patientName: data.patientName || 'Paciente',
  notes: data.notes ?? null,
  createdByUserId: data.createdByUserId ?? null,
  assessmentDateIso: data.assessmentDateIso,
  patientContext: {
    birthDate: data.patientContext?.birthDate ?? null,
    ageYears: typeof data.patientContext?.ageYears === 'number' ? data.patientContext.ageYears : null,
    sex: data.patientContext?.sex ?? null,
    weightKg: typeof data.patientContext?.weightKg === 'number' ? data.patientContext.weightKg : null,
    heightCm: typeof data.patientContext?.heightCm === 'number' ? data.patientContext.heightCm : null,
    identifier: data.patientContext?.identifier ?? null,
  },
  professionalContext: {
    userId: data.professionalContext?.userId ?? null,
    name: data.professionalContext?.name ?? null,
    registration: data.professionalContext?.registration ?? null,
    role: data.professionalContext?.role ?? null,
  },
  existingResults: Array.isArray(data.existingResults) ? data.existingResults.map(normalizeResult) : [],
});

const ensureBrowserCrypto = (): Crypto => {
  if (typeof crypto === 'undefined' || !crypto.subtle || !crypto.getRandomValues) {
    throw new Error('API criptográfica não disponível neste ambiente.');
  }

  return crypto;
};

const generateToken = (): string => {
  const browserCrypto = ensureBrowserCrypto();
  const bytes = new Uint8Array(PUBLIC_INVITE_TOKEN_BYTES);
  browserCrypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
};

const sha256Hex = async (value: string): Promise<string> => {
  const browserCrypto = ensureBrowserCrypto();
  const digest = await browserCrypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (chunk) => chunk.toString(16).padStart(2, '0')).join('');
};

const normalizeBirthDate = (birthDate: string): string => {
  const trimmed = birthDate.trim();
  if (!trimmed) {
    throw new Error('Data de nascimento obrigatória para validação do link.');
  }

  return trimmed.slice(0, 10);
};

const buildInviteUrl = (tenantSlug: string, token: string): string => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  if (!origin) {
    throw new Error('Não foi possível determinar a origem atual para montar o link.');
  }

  return `${origin}${buildTenantPath(`/q/${token}`, tenantSlug)}`;
};

const describeSupabaseError = (error: any, fallback: string): string => {
  const parts = [error?.message, error?.details, error?.hint]
    .filter((value) => typeof value === 'string' && value.trim().length > 0)
    .map((value) => value.trim());

  return parts.length ? parts.join(' | ') : fallback;
};

const logSupabaseError = (context: string, error: any) => {
  console.error(context, {
    code: error?.code ?? null,
    message: error?.message ?? null,
    details: error?.details ?? null,
    hint: error?.hint ?? null,
    error,
  });
};

export class AssessmentInvitesV2Service {
  static async create(params: {
    clientId: string;
    clientSlug: string;
    patientId: string;
    patientBirthDate: string;
    selectedTests: AssessmentV2TestKey[];
    sessionDate: string;
    notes?: string | null;
    createdByUserId?: string | null;
    professionalContext?: AssessmentProfessionalContext;
    metadataJson?: Record<string, any>;
    expiresAt?: string | null;
  }): Promise<{
    invite: AssessmentInviteV2;
    sessionId: string;
    token: string;
    url: string;
  }> {
    const selectedTests = Array.from(new Set(params.selectedTests));
    if (!selectedTests.length) {
      throw new Error('Selecione ao menos um questionário para gerar o link.');
    }

    if (!selectedTests.every((testKey) => isAssessmentV2QuestionnaireTest(testKey))) {
      throw new Error('O link público suporta apenas questionários autoaplicáveis nesta etapa.');
    }

    normalizeBirthDate(params.patientBirthDate);

    if (!params.clientSlug?.trim()) {
      throw new Error('Slug do tenant não disponível para montar o link público.');
    }

    const session = await AssessmentSessionsV2Service.create({
      clientId: params.clientId,
      patientId: params.patientId,
      sessionDate: params.sessionDate,
      notes: params.notes || null,
      selectedTests,
      status: 'draft',
      createdByUserId: params.createdByUserId ?? null,
    });

    const token = generateToken();
    const tokenHash = await sha256Hex(token);
    const expiresAt =
      params.expiresAt ??
      new Date(Date.now() + DEFAULT_INVITE_TTL_HOURS * 60 * 60 * 1000).toISOString();

    const metadataJson = {
      ...(params.metadataJson ?? {}),
      professionalContext: params.professionalContext ?? {
        userId: params.createdByUserId ?? null,
        name: null,
        registration: null,
        role: null,
      },
    };

    let data: any = null;
    try {
      const insertResult = await supabase
        .from('assessment_invites_v2')
        .insert({
          clientId: params.clientId,
          sessionId: session.sessionId,
          patientId: params.patientId,
          tokenHash,
          selectedTests,
          status: 'active',
          expiresAt,
          createdByUserId: params.createdByUserId ?? null,
          metadataJson,
        })
        .select('*')
        .single();

      if (insertResult.error) {
        throw insertResult.error;
      }

      data = insertResult.data;
    } catch (error) {
      console.error('Erro ao criar assessment_invites_v2:', error);
      await AssessmentSessionsV2Service.delete(session.sessionId, params.clientId).catch((cleanupError) => {
        console.error('Erro ao limpar sessão V2 órfã após falha ao criar invite:', cleanupError);
      });
      throw error;
    }

    return {
      invite: normalizeInvite(data),
      sessionId: session.sessionId,
      token,
      url: buildInviteUrl(params.clientSlug, token),
    };
  }

  static async revoke(inviteId: string, clientId: string, reason?: string | null): Promise<AssessmentInviteV2> {
    const { data, error } = await supabase
      .from('assessment_invites_v2')
      .update({
        status: 'revoked',
        revokedAt: new Date().toISOString(),
        revokedReason: reason || null,
        updatedAt: new Date().toISOString(),
      })
      .eq('inviteId', inviteId)
      .eq('clientId', clientId)
      .select('*')
      .single();

    if (error) {
      console.error('Erro ao revogar assessment_invites_v2:', error);
      throw error;
    }

    return normalizeInvite(data);
  }

  static async getById(inviteId: string, clientId: string): Promise<AssessmentInviteV2> {
    const { data, error } = await supabase
      .from('assessment_invites_v2')
      .select('*')
      .eq('inviteId', inviteId)
      .eq('clientId', clientId)
      .single();

    if (error) {
      logSupabaseError('Erro ao buscar assessment_invites_v2 por id:', error);
      throw new Error(describeSupabaseError(error, 'Falha ao acompanhar o status do link público.'));
    }

    return normalizeInvite(data);
  }

  static async getPublicPreview(tenantSlug: string, token: string): Promise<AssessmentInviteV2Preview> {
    const { data, error } = await supabase.rpc('get_public_assessment_invite_v2', {
      p_slug: tenantSlug,
      p_token: token,
    });

    if (error) {
      logSupabaseError('Erro ao obter preview público do invite:', error);
      throw new Error(describeSupabaseError(error, 'Falha ao carregar o link público da avaliação.'));
    }

    return normalizePreview(data);
  }

  static async startPublicInvite(
    tenantSlug: string,
    token: string,
    birthDate: string,
  ): Promise<AssessmentInviteV2StartPayload> {
    const { data, error } = await supabase.rpc('start_public_assessment_invite_v2', {
      p_slug: tenantSlug,
      p_token: token,
      p_birth_date: normalizeBirthDate(birthDate),
    });

    if (error) {
      logSupabaseError('Erro ao iniciar invite público:', error);
      throw new Error(describeSupabaseError(error, 'Falha ao validar o acesso ao link público.'));
    }

    return normalizeStartPayload(data);
  }

  static async submitPublicStep(params: {
    tenantSlug: string;
    token: string;
    birthDate: string;
    testKey: AssessmentV2TestKey;
    assessmentDate: string;
    notes?: string | null;
    summaryJson: Record<string, any>;
    payloadJson: Record<string, any>;
    assetsJson?: Record<string, any>;
  }): Promise<{ resultId: string; testKey: string; groupIndex: number; groupTotal: number; status: string }> {
    const { data, error } = await supabase.rpc('submit_public_assessment_step_v2', {
      p_slug: params.tenantSlug,
      p_token: params.token,
      p_birth_date: normalizeBirthDate(params.birthDate),
      p_test_key: params.testKey,
      p_assessment_date: params.assessmentDate,
      p_summary: params.summaryJson ?? {},
      p_payload: params.payloadJson ?? {},
      p_assets: params.assetsJson ?? {},
      p_notes: params.notes ?? null,
    });

    if (error) {
      logSupabaseError('Erro ao enviar etapa pública do invite:', error);
      throw new Error(describeSupabaseError(error, `Falha ao salvar ${params.testKey}.`));
    }

    return {
      resultId: data.resultId,
      testKey: data.testKey,
      groupIndex: data.groupIndex,
      groupTotal: data.groupTotal,
      status: data.status,
    };
  }

  static async completePublicInvite(
    tenantSlug: string,
    token: string,
    birthDate: string,
  ): Promise<{ status: string; sessionId: string; inviteId: string }> {
    const { data, error } = await supabase.rpc('complete_public_assessment_invite_v2', {
      p_slug: tenantSlug,
      p_token: token,
      p_birth_date: normalizeBirthDate(birthDate),
    });

    if (error) {
      logSupabaseError('Erro ao concluir invite público:', error);
      throw new Error(describeSupabaseError(error, 'Falha ao concluir o link público da avaliação.'));
    }

    return {
      status: data.status,
      sessionId: data.sessionId,
      inviteId: data.inviteId,
    };
  }
}
